/**
 * Meta (Facebook) Pixel tracking service
 *
 * Provides methods for initializing Meta Pixel and tracking events
 * for conversion tracking and remarketing.
 *
 * @see https://developers.facebook.com/docs/meta-pixel/implementation
 */

// Meta Pixel ID
const META_PIXEL_ID = '1565427271332406';

// Track if pixel has been initialized
let isPixelInitialized = false;

// Extend Window interface for fbq
declare global {
  interface Window {
    fbq: any;
    _fbq: any;
  }
}

/**
 * Initialize the Meta Pixel
 * Should be called once when the app/page loads
 */
export const initMetaPixel = (): void => {
  if (typeof window === 'undefined') return;
  if (isPixelInitialized) return;

  try {
    // Meta Pixel base code
    (function(f: any, b: Document, e: string, v: string) {
      let n: any, t: HTMLScriptElement, s: HTMLScriptElement | null;
      if (f.fbq) return;
      n = f.fbq = function() {
        n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
      };
      if (!f._fbq) f._fbq = n;
      n.push = n;
      n.loaded = true;
      n.version = '2.0';
      n.queue = [];
      t = b.createElement(e) as HTMLScriptElement;
      t.async = true;
      t.src = v;
      s = b.getElementsByTagName(e)[0] as HTMLScriptElement | null;
      if (s && s.parentNode) {
        s.parentNode.insertBefore(t, s);
      }
    })(window, document, 'script', 'https://connect.facebook.net/en_US/fbevents.js');

    // Initialize with Pixel ID
    window.fbq('init', META_PIXEL_ID);

    isPixelInitialized = true;

    if (import.meta.env.DEV) {
      console.log('[MetaPixel] Initialized with ID:', META_PIXEL_ID);
    }
  } catch (error) {
    if (import.meta.env.DEV) {
      console.warn('[MetaPixel] Failed to initialize:', error);
    }
  }
};

/**
 * Track a PageView event
 * Should be called when navigating to a new page
 */
export const trackPageView = (): void => {
  if (typeof window === 'undefined' || !window.fbq) {
    // Try to initialize if not done
    initMetaPixel();
  }

  try {
    if (window.fbq) {
      window.fbq('track', 'PageView');
      if (import.meta.env.DEV) {
        console.log('[MetaPixel] PageView tracked');
      }
    }
  } catch (error) {
    if (import.meta.env.DEV) {
      console.warn('[MetaPixel] Failed to track PageView:', error);
    }
  }
};

/**
 * Track ViewContent event
 * For when a user views a product page
 */
export const trackViewContent = (params: {
  content_name?: string;
  content_ids?: string[];
  content_type?: string;
  value?: number;
  currency?: string;
}): void => {
  if (typeof window === 'undefined' || !window.fbq) return;

  try {
    window.fbq('track', 'ViewContent', {
      content_name: params.content_name,
      content_ids: params.content_ids,
      content_type: params.content_type || 'product',
      value: params.value,
      currency: params.currency || 'USD',
    });
    if (import.meta.env.DEV) {
      console.log('[MetaPixel] ViewContent tracked:', params);
    }
  } catch (error) {
    if (import.meta.env.DEV) {
      console.warn('[MetaPixel] Failed to track ViewContent:', error);
    }
  }
};

/**
 * Track InitiateCheckout event
 * For when a user starts the checkout process
 */
export const trackInitiateCheckout = (params: {
  content_ids?: string[];
  content_name?: string;
  value?: number;
  currency?: string;
  num_items?: number;
}): void => {
  if (typeof window === 'undefined' || !window.fbq) return;

  try {
    window.fbq('track', 'InitiateCheckout', {
      content_ids: params.content_ids,
      content_name: params.content_name,
      value: params.value,
      currency: params.currency || 'USD',
      num_items: params.num_items || 1,
    });
    if (import.meta.env.DEV) {
      console.log('[MetaPixel] InitiateCheckout tracked:', params);
    }
  } catch (error) {
    if (import.meta.env.DEV) {
      console.warn('[MetaPixel] Failed to track InitiateCheckout:', error);
    }
  }
};

/**
 * Track AddPaymentInfo event
 * For when a user adds payment information
 */
export const trackAddPaymentInfo = (params?: {
  content_ids?: string[];
  value?: number;
  currency?: string;
}): void => {
  if (typeof window === 'undefined' || !window.fbq) return;

  try {
    window.fbq('track', 'AddPaymentInfo', {
      content_ids: params?.content_ids,
      value: params?.value,
      currency: params?.currency || 'USD',
    });
    if (import.meta.env.DEV) {
      console.log('[MetaPixel] AddPaymentInfo tracked:', params);
    }
  } catch (error) {
    if (import.meta.env.DEV) {
      console.warn('[MetaPixel] Failed to track AddPaymentInfo:', error);
    }
  }
};

/**
 * Track Purchase event (CONVERSION)
 * For when a user completes a purchase - this is the main conversion event
 */
export const trackPurchase = (params: {
  value: number;
  currency?: string;
  content_ids?: string[];
  content_name?: string;
  content_type?: string;
  num_items?: number;
}): void => {
  if (typeof window === 'undefined' || !window.fbq) return;

  try {
    window.fbq('track', 'Purchase', {
      value: params.value,
      currency: params.currency || 'USD',
      content_ids: params.content_ids,
      content_name: params.content_name,
      content_type: params.content_type || 'product',
      num_items: params.num_items || 1,
    });
    if (import.meta.env.DEV) {
      console.log('[MetaPixel] Purchase tracked:', params);
    }
  } catch (error) {
    if (import.meta.env.DEV) {
      console.warn('[MetaPixel] Failed to track Purchase:', error);
    }
  }
};

/**
 * Track a custom event
 */
export const trackCustomEvent = (eventName: string, params?: Record<string, any>): void => {
  if (typeof window === 'undefined' || !window.fbq) return;

  try {
    window.fbq('trackCustom', eventName, params);
    if (import.meta.env.DEV) {
      console.log('[MetaPixel] Custom event tracked:', eventName, params);
    }
  } catch (error) {
    if (import.meta.env.DEV) {
      console.warn('[MetaPixel] Failed to track custom event:', error);
    }
  }
};

// Export all methods as a namespace for convenience
export const metaPixel = {
  init: initMetaPixel,
  pageView: trackPageView,
  viewContent: trackViewContent,
  initiateCheckout: trackInitiateCheckout,
  addPaymentInfo: trackAddPaymentInfo,
  purchase: trackPurchase,
  trackCustom: trackCustomEvent,
};

export default metaPixel;
